//
/* GS://PROTOCOLO
 * Arquivo: src/components/forms/SupplierForm.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 1.2.0 – FIX: Atualização de Tipos de Fornecedor
 * Impacto da Versão: [src/components/forms/SupplierForm.js]
 * Data: 21/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - ATUALIZADO: Lista de opções do campo "Tipo de Parceiro" conforme solicitação do usuário (9 categorias).
 */

import React, { useState, useEffect } from 'react';
import { Save, MapPin, Phone, Building2, Package } from 'lucide-react';

const SupplierForm = ({ onClose, onSave, supplierData }) => {
  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    phone: '',
    email: '',
    type: 'PRODUTO_ACABADO', // Novo valor padrão
    address: {
      street: '',
      number: '',
      neighborhood: '',
      city: '',
      state: 'SP'
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (supplierData) {
      setFormData({
        name: supplierData.name || '',
        cnpj: supplierData.cnpj || '',
        phone: supplierData.phone || '',
        email: supplierData.email || '',
        type: supplierData.type || 'PRODUTO_ACABADO',
        address: {
          street: supplierData.address?.street || '',
          number: supplierData.address?.number || '',
          neighborhood: supplierData.address?.neighborhood || '',
          city: supplierData.address?.city || '',
          state: supplierData.address?.state || ''
        }
      });
    }
  }, [supplierData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('address.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSave(formData, supplierData?.id);
    } catch (error) {
      console.error("Erro no formulário:", error);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg">
      <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[80vh]">
        
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-500 uppercase border-b pb-1 flex items-center gap-2">
            <Building2 size={16} /> Dados Cadastrais
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Fornecedor *</label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-gold focus:border-brand-gold outline-none"
                placeholder="Ex: Tati Semijoias"
              />
            </div>

            {/* [GS-FIX] Novas Opções de Tipo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Package size={14} /> Tipo de Parceiro *
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-gold focus:border-brand-gold outline-none bg-white"
              >
                <option value="PRODUTO_ACABADO">1 - Produto Acabado </option>
                <option value="PECA_PRONTA">2 - Peça Pronta</option>
                <option value="REVENDA">3 - Revenda / Distribuidor</option>
                <option value="ATACADISTA">4 - Atacadista (Prontas)</option>
                <option value="IMPORTADOR">5 - Importador / Distribuidor</option>
                <option value="MISTO">6 - Peça em Bruto</option>
                <option value="GALVANOPLASTIA">7 - Galvanoplastia</option>
                <option value="SERVICOS">8 - Serviços</option>
                <option value="OUTROS">9 - Outros</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CNPJ / CPF</label>
              <input
                type="text"
                name="cnpj"
                value={formData.cnpj}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-gold focus:border-brand-gold outline-none"
                placeholder="00.000.000/0000-00"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Phone size={14} /> Telefone / WhatsApp *
              </label>
              <input
                type="text"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-gold focus:border-brand-gold outline-none"
                placeholder="(00) 00000-0000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-gold focus:border-brand-gold outline-none"
                placeholder="contato@fornecedor.com"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-500 uppercase border-b pb-1 flex items-center gap-2">
            <MapPin size={16} /> Endereço
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Rua / Avenida *</label>
              <input
                type="text"
                name="address.street"
                required
                value={formData.address.street}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-gold focus:border-brand-gold outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Número *</label>
              <input
                type="text"
                name="address.number"
                required
                value={formData.address.number}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-gold focus:border-brand-gold outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bairro *</label>
              <input
                type="text"
                name="address.neighborhood"
                required
                value={formData.address.neighborhood}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-gold focus:border-brand-gold outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cidade *</label>
              <input
                type="text"
                name="address.city"
                required
                value={formData.address.city}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-gold focus:border-brand-gold outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado (UF) *</label>
              <input
                type="text"
                name="address.state"
                required
                maxLength={2}
                value={formData.address.state}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-gold focus:border-brand-gold outline-none uppercase"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-brand-gold text-brand-black font-bold rounded hover:bg-yellow-500 transition-colors flex items-center gap-2"
            disabled={isSubmitting}
          >
            <Save size={18} />
            {isSubmitting ? 'Salvando...' : 'Salvar Fornecedor'}
          </button>
        </div>

      </form>
    </div>
  );
};

export default SupplierForm;